import React from 'react';
import clsx from 'clsx';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import IconButton from '@material-ui/core/IconButton';
import Switch from '@material-ui/core/Switch';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import { fade, makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import green from '@material-ui/core/colors/green';
import red from '@material-ui/core/colors/red';
import moment from 'moment';
import { UserAvatar, UserRolePill, resolveRoleKey, roleLabel, ROLE_NONE } from '../../Settings/components/users/UserRolePill';
import { UserListEmpty } from './UserListStates';

const formatDateTime = (val) => {
	if (!val) return '-';
	var m = moment(val);
	return m.isValid() ? m.format('D MMMM YYYY h:mmA') : '-';
};

const useStyles = makeStyles((theme) => {
	const isDark = theme.palette.type === 'dark';
	const line = fade(theme.palette.text.primary, 0.12);
	const line2 = fade(theme.palette.text.primary, isDark ? 0.08 : 0.07);
	const soft = fade(theme.palette.text.primary, isDark ? 0.07 : 0.05);
	return {
		card: {
			borderRadius: 16,
			border: `1px solid ${line2}`,
			boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.04)',
			backgroundColor: theme.palette.background.paper,
			overflow: 'hidden',
			display: 'flex',
			flexDirection: 'column'
		},
		scroller: {
			overflowX: 'auto',
			scrollbarWidth: 'thin'
		},
		table: {
			tableLayout: 'auto'
		},
		headCell: {
			height: 44,
			padding: '0 16px',
			fontSize: 11,
			fontWeight: 700,
			letterSpacing: '0.08em',
			textTransform: 'uppercase',
			whiteSpace: 'nowrap',
			color: theme.palette.text.secondary,
			backgroundColor: fade(theme.palette.text.primary, isDark ? 0.03 : 0.02),
			borderBottom: `1px solid ${line}`
		},
		cell: {
			height: 56,
			padding: '0 16px',
			fontSize: 13,
			color: theme.palette.text.primary,
			borderBottom: `1px solid ${line2}`
		},
		row: {
			transition: theme.transitions.create('background-color', { duration: 120 }),
			'&:hover': {
				backgroundColor: fade(theme.palette.text.primary, isDark ? 0.05 : 0.03)
			},
			'&:last-child $cell': {
				borderBottom: 0
			}
		},
		indexCell: {
			width: 28,
			paddingRight: 0,
			color: theme.palette.text.secondary,
			fontVariantNumeric: 'tabular-nums'
		},
		user: {
			display: 'flex',
			alignItems: 'center',
			gap: 10,
			minWidth: 0
		},
		avatarWrap: {
			display: 'inline-flex',
			flex: 'none',
			transition: 'opacity 150ms'
		},
		avatarDim: {
			opacity: 0.5
		},
		userText: {
			display: 'flex',
			flexDirection: 'column',
			minWidth: 0,
			lineHeight: 1.3
		},
		name: {
			fontWeight: 600,
			fontSize: 13,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			maxWidth: 260
		},
		username: {
			fontSize: 12,
			color: theme.palette.text.secondary,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			maxWidth: 260
		},
		muted: {
			color: theme.palette.text.secondary
		},
		statusPill: {
			height: 22,
			padding: '0 8px',
			borderRadius: 999,
			display: 'inline-flex',
			alignItems: 'center',
			fontSize: 11,
			fontWeight: 700,
			lineHeight: 1,
			whiteSpace: 'nowrap',
			transition: 'background-color 200ms, color 200ms'
		},
		statusActive: {
			backgroundColor: fade(green[500], isDark ? 0.2 : 0.14),
			color: isDark ? green[300] : green[800]
		},
		statusInactive: {
			backgroundColor: fade(red[500], isDark ? 0.22 : 0.12),
			color: isDark ? red[300] : red[700]
		},
		subLine: {
			display: 'block',
			marginTop: 4,
			fontSize: 11,
			color: theme.palette.text.secondary,
			whiteSpace: 'nowrap'
		},
		dateCell: {
			whiteSpace: 'nowrap',
			fontVariantNumeric: 'tabular-nums'
		},
		hideXs: {
			[theme.breakpoints.down('xs')]: { display: 'none' }
		},
		actionsCell: {
			width: 96,
			paddingLeft: 8,
			paddingRight: 12
		},
		actions: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'flex-end',
			gap: 4
		},
		iconButton: {
			width: 32,
			height: 32,
			borderRadius: 8,
			color: theme.palette.text.secondary,
			transition: theme.transitions.create(['background-color', 'color', 'transform'], { duration: 150 }),
			'&:hover': { backgroundColor: soft, color: theme.palette.text.primary },
			'&:active': { transform: 'scale(0.94)' },
			'& .MuiIconButton-label svg': { display: 'block', fontSize: 18 }
		},
		switchWrap: {
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center',
			minWidth: 48,
			height: 32
		},
		spinner: {
			color: theme.palette.text.secondary
		},
		pagination: {
			borderTop: `1px solid ${line}`,
			'& .MuiTablePagination-toolbar': {
				minHeight: 52,
				fontSize: 13,
				color: theme.palette.text.secondary
			},
			'& .MuiTablePagination-caption': {
				fontSize: 13
			},
			'& .MuiTablePagination-select': {
				fontSize: 13,
				fontWeight: 500,
				color: theme.palette.text.primary
			},
			'& .MuiTablePagination-actions': {
				marginLeft: 12
			}
		}
	};
});

export default function UserListTable({
	language,
	rows,
	count,
	page,
	rowsPerPage,
	onPageChange,
	onRowsPerPageChange,
	onEdit,
	onToggleStatus,
	togglingId,
	showClear,
	onClear
}) {
	const classes = useStyles();

	return (
		<div className={classes.card}>
			{count === 0 ? (
				<UserListEmpty language={language} showClear={showClear} onClear={onClear} />
			) : (
				<React.Fragment>
					<div className={classes.scroller}>
						<Table className={classes.table} size="small" aria-label={language.users}>
							<TableHead>
								<TableRow>
									<TableCell className={clsx(classes.headCell, classes.indexCell)}>#</TableCell>
									<TableCell className={classes.headCell}>{language.name_label}</TableCell>
									<TableCell className={classes.headCell}>{language.user_role}</TableCell>
									<TableCell className={classes.headCell}>{language.status}</TableCell>
									<TableCell className={clsx(classes.headCell, classes.hideXs)}>{language.created_date}</TableCell>
									<TableCell className={clsx(classes.headCell, classes.hideXs)}>{language.last_updated_date}</TableCell>
									<TableCell className={clsx(classes.headCell, classes.actionsCell)} />
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.map((row, index) => {
									var active = row.active_status == 1;
									var roleKey = resolveRoleKey(row.role_name, row.position);
									var roleText = roleLabel(roleKey, row.role_name, language);
									var toggling = String(togglingId) === String(row.login_id);
									var toggleLabel = active ? language.deactivate : language.activate;
									return (
										<TableRow key={row.login_id || index} className={classes.row}>
											<TableCell className={clsx(classes.cell, classes.indexCell)}>
												{page * rowsPerPage + index + 1}
											</TableCell>
											<TableCell className={classes.cell}>
												<div className={classes.user}>
													<span className={clsx(classes.avatarWrap, !active && classes.avatarDim)}>
														<UserAvatar name={row.name || row.username} roleKey={roleKey} />
													</span>
													<div className={classes.userText}>
														<span className={classes.name}>{row.name}</span>
														<span className={classes.username}>@{row.username}</span>
													</div>
												</div>
											</TableCell>
											<TableCell className={classes.cell}>
												{roleKey === ROLE_NONE
													? <span className={classes.muted}>-</span>
													: <UserRolePill roleKey={roleKey} label={roleText} small />
												}
											</TableCell>
											<TableCell className={classes.cell}>
												<span className={clsx(classes.statusPill, active ? classes.statusActive : classes.statusInactive)}>
													{active ? language.active : language.inactive}
												</span>
												{!active && (
													<span className={classes.subLine}>
														{language.inactive_date}: {formatDateTime(row.inactive_date)}
													</span>
												)}
											</TableCell>
											<TableCell className={clsx(classes.cell, classes.dateCell, classes.hideXs)}>
												{formatDateTime(row.created_at)}
											</TableCell>
											<TableCell className={clsx(classes.cell, classes.dateCell, classes.hideXs)}>
												{formatDateTime(row.updated_at)}
												{row.updated_by ? (
													<span className={classes.subLine}>{language.by} {row.updated_by}</span>
												) : null}
											</TableCell>
											<TableCell className={clsx(classes.cell, classes.actionsCell)}>
												<div className={classes.actions}>
													<Tooltip title={language.edit_user}>
														<IconButton
															className={classes.iconButton}
															aria-label={language.edit_user}
															onClick={() => onEdit(row.login_id)}
														>
															<EditIcon />
														</IconButton>
													</Tooltip>
													<Tooltip title={toggleLabel}>
														<span className={classes.switchWrap}>
															{toggling ? (
																<CircularProgress size={18} className={classes.spinner} />
															) : (
																<Switch
																	size="small"
																	color="primary"
																	checked={active}
																	onChange={() => onToggleStatus(row.login_id, row.active_status)}
																	inputProps={{ 'aria-label': toggleLabel }}
																/>
															)}
														</span>
													</Tooltip>
												</div>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</div>
					<TablePagination
						component="div"
						className={classes.pagination}
						count={count}
						page={page}
						rowsPerPage={rowsPerPage}
						onPageChange={onPageChange}
						onRowsPerPageChange={onRowsPerPageChange}
						rowsPerPageOptions={[10, 25, 50]}
					/>
				</React.Fragment>
			)}
		</div>
	);
}
